import { fetchAPI } from '../../../services/apiClient';

// ========================
// Types
// ========================

export interface InventarioItem {
  nombre: string;
  cantidad: number;
}

export interface AperturaInfo {
  tiene_historial: boolean;
  ultimo_cierre: {
    id: string;
    fecha: string;
    efectivo_contado: string;
    efectivo_esperado: string;
    diferencia: string;
    estado_diferencia: string;
    inventario_final: {
      nombre: string;
      cantidad_final: number;
      cantidad_inicial: number;
      costo_unitario: string;
    }[];
    total_ingresos: string;
    total_egresos: string;
  } | null;
  sugerencia: {
    efectivo_inicial_sugerido: string;
    inventario_inicial_sugerido: InventarioItem[];
  } | null;
}

export interface VentaSorteoItem {
  sorteo_id: string;
  sorteo_nombre: string;
  total_vendido: string;
}

export interface ResumenSistema {
  fecha: string;
  vendedor_id: string;
  vendedor_nombre: string;
  ventas: {
    sorteos: VentaSorteoItem[];
    total_ventas_loteria: string;
  };
  premios: {
    total_premios_pagados: string;
    detalle: {
      ticket_id: string;
      ticket_number: string | null;
      monto: string;
      sorteo_id: string | null;
    }[];
  };
  creditos: {
    total_credito_vendido: string;
    total_credito_cobrado: string;
  };
  recargas: {
    total_compras_recarga: string;
  };
  pagos_digitales: {
    total_sinpe: string;
    total_transferencia: string;
    total_digital: string;
  };
  loterias_fisicas: {
    asignaciones: LoteriaAsignada[];
    tiene_sesion: boolean;
  };
}

export interface LoteriaAsignada {
  juego_id: number;
  nombre: string;
  entregado: number;
  devuelto: number;
  neto: number;
  valor_unitario: number;
}

export interface ValeInput {
  monto: string;
  justificacion: string;
}

export interface IngresoExtraInput {
  concepto: string;
  monto: string;
}

export interface LoteriaFisicaInput {
  juego_id: number;
  cantidad_final: number;
}

export interface CierreVendedorInput {
  fecha: string;
  vales: ValeInput[];
  ingresos_extra: IngresoExtraInput[];
  loteria_fisica: LoteriaFisicaInput[];
  efectivo_contado: string;
  notas?: string;
}

export interface CierreVendedorResult {
  id: string;
  vendedor_id: string;
  fecha: string;
  total_ventas_loteria: string;
  total_premios_pagados: string;
  total_credito_vendido: string;
  recargas_monto_inicial: string;
  recargas_monto_final: string;
  total_recargas: string;
  total_vales: string;
  total_ingresos_extra: string;
  total_loteria_fisica: string;
  total_ingresos: string;
  total_egresos: string;
  saldo_teorico: string;
  efectivo_contado: string;
  diferencia: string;
  estado_diferencia: string;
}

export interface CierreHistorico {
  id: string;
  fecha: string;
  total_ingresos: string;
  total_egresos: string;
  saldo_teorico: string;
  efectivo_contado: string;
  diferencia: string;
  estado_diferencia: string;
  created_at: string | null;
  anulado: boolean;
  anulado_por: string | null;
  anulado_at: string | null;
  motivo_anulacion: string | null;
}

export interface EstadoCierre {
  fecha: string;
  cerrada: boolean;
  cierre_id: string | null;
  caja_abierta: boolean;
  caja_sesion_id: string | null;
  caja_estado: 'ABIERTA' | 'CERRADA' | 'NO_INICIADA';
  saldo_inicial: string;
  inventario_inicial: InventarioItem[];
  // Campos de continuidad (§2.3 AGENTS.md)
  apertura_numero: number;
  total_aperturas_hoy: number;
  total_aperturas_permitidas: number;
  puede_abrir_mas: boolean;
}

export interface CajaResumenOperativo {
  fecha_desde: string | null;
  fecha_hasta: string | null;
  total_ingresos: string;
  total_egresos: string;
  saldo_actual: string;
  caja_abierta: boolean;
  caja_sesion_id: string | null;
}

export interface CajaMovimiento {
  id: string;
  tipo: string;
  naturaleza: 'INGRESO' | 'EGRESO';
  monto: string;
  descripcion: string;
  referencia_tipo: string | null;
  referencia_id: string | null;
  fecha_operativa: string;
  created_at: string | null;
}

export interface SesionHoy {
  sesion_id: string;
  vendedor_id: string;
  vendedor_nombre: string;
  estado: string;
  saldo_inicial: string;
  abierta_at: string | null;
}

// ========================
// API calls
// ========================

export async function getResumenSistema(fecha: string): Promise<ResumenSistema> {
  return fetchAPI<ResumenSistema>(`/caja-vendedor/resumen-sistema?fecha=${fecha}`);
}

export async function cerrarCajaVendedor(data: CierreVendedorInput): Promise<CierreVendedorResult> {
  return fetchAPI<CierreVendedorResult>('/caja-vendedor/cerrar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function getCierresHistoricos(limit: number = 30): Promise<CierreHistorico[]> {
  return fetchAPI<CierreHistorico[]>(`/caja-vendedor/cierres?limit=${limit}`);
}

export async function getEstadoCierre(fecha: string): Promise<EstadoCierre> {
  return fetchAPI<EstadoCierre>(`/caja-vendedor/estado?fecha=${fecha}`);
}

export async function getResumenOperativo(
  fechaDesde?: string,
  fechaHasta?: string,
): Promise<CajaResumenOperativo> {
  const params: string[] = [];
  if (fechaDesde) params.push(`fecha_desde=${fechaDesde}`);
  if (fechaHasta) params.push(`fecha_hasta=${fechaHasta}`);
  const qs = params.length ? `?${params.join('&')}` : '';
  return fetchAPI<CajaResumenOperativo>(`/caja-vendedor/resumen-operativo${qs}`);
}

export async function getMovimientosCaja(
  fechaDesde?: string,
  fechaHasta?: string,
  limit: number = 100,
): Promise<CajaMovimiento[]> {
  const params: string[] = [`limit=${limit}`];
  if (fechaDesde) params.push(`fecha_desde=${fechaDesde}`);
  if (fechaHasta) params.push(`fecha_hasta=${fechaHasta}`);
  return fetchAPI<CajaMovimiento[]>(`/caja-vendedor/movimientos?${params.join('&')}`);
}

export interface MovimientoResumen {
  fecha_operativa: string;
  tipo: string;
  descripcion: string;
  monto: string;
  naturaleza: 'INGRESO' | 'EGRESO';
  cantidad: number;
  id: string | null;
  referencia_tipo: string | null;
}

export async function getMovimientosResumen(
  fechaDesde?: string,
  fechaHasta?: string,
): Promise<MovimientoResumen[]> {
  const params: string[] = [];
  if (fechaDesde) params.push(`fecha_desde=${fechaDesde}`);
  if (fechaHasta) params.push(`fecha_hasta=${fechaHasta}`);
  const qs = params.length ? `?${params.join('&')}` : '';
  return fetchAPI<MovimientoResumen[]>(`/caja-vendedor/movimientos/agrupados${qs}`);
}

export async function getDetalleVentasFecha(fecha: string): Promise<CajaMovimiento[]> {
  return fetchAPI<CajaMovimiento[]>(`/caja-vendedor/movimientos/detalle-ventas?fecha=${fecha}`);
}

export async function getDetalleCierre(cierreId: string): Promise<any> {
  return fetchAPI<any>(`/caja-vendedor/cierre/${cierreId}`);
}

export async function anularCierre(cierreId: string, motivo: string): Promise<{ ok: boolean; message: string }> {
  return fetchAPI<{ ok: boolean; message: string }>(`/caja-vendedor/cierre/${cierreId}/anular`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ motivo }),
  });
}

export async function getAperturaInfo(): Promise<AperturaInfo> {
  return fetchAPI<AperturaInfo>('/caja-vendedor/apertura-info');
}

export async function abrirCaja(
  saldoInicial: number = 0,
  inventarioInicial?: InventarioItem[],
): Promise<{
  ok: boolean;
  message: string;
  sesion_id: string;
  estado: string;
  saldo_inicial: string;
  apertura_numero: number;
  total_aperturas_permitidas: number;
  advertencia?: string;
}> {
  return fetchAPI('/caja-vendedor/abrir', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      saldo_inicial: saldoInicial,
      inventario_inicial: inventarioInicial ?? [],
    }),
  });
}

export async function getSesionesHoy(): Promise<{ fecha: string; sesiones: SesionHoy[] }> {
  return fetchAPI('/caja-vendedor/sesiones-hoy');
}

export async function editarSaldoInicial(vendedorId: string, saldoInicial: number): Promise<{ ok: boolean; message: string }> {
  return fetchAPI('/caja-vendedor/sesion-saldo', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ vendedor_id: vendedorId, saldo_inicial: saldoInicial }),
  });
}

export async function reabrirCaja(vendedorId: string, motivo: string): Promise<{
  ok: boolean;
  message: string;
  sesion_id?: string;
  apertura_numero?: number;
  total_aperturas_permitidas?: number;
  reopen_numero?: number;
  advertencia?: string;
}> {
  return fetchAPI('/caja-vendedor/reabrir', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ vendedor_id: vendedorId, motivo }),
  });
}

export async function devolucionLoteria(juego_id: number, cantidad: number): Promise<{ ok: boolean; juego_id: number; devuelto: number; neto_restante: number }> {
  return fetchAPI('/caja-vendedor/devolucion-loteria', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ juego_id, cantidad }),
  });
}
